import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AppService } from 'src/@core/services/app.service';
import { FunnelService } from './funnel.service';
import {
  addStage, allPlaced, applyPlacements, bandWidth, displayStages, FunnelConfig, FunnelStage, FunnelView, keyStages,
  MAX_STAGE_NAME, MAX_STAGES, moveOutcomes, moveStageInView, OUT_STAGE, removeStage, renameStage, setStageKey,
  unmappedOutcomes
} from './funnel.logic';

/**
 * Step 4 of project setup: write the funnel stages and place every outcome in one.
 * The parent owns the config and the save; this emits a new config on every change.
 * List view shows P0 on top. Funnel view shows the same stages as bands, cold on top.
 */
@Component({
  selector: 'hour4u-admin-funnel-builder',
  templateUrl: './funnel-builder.component.html',
  styleUrls: ['./funnel-builder.component.scss']
})
export class FunnelBuilderComponent {
  @Input() config: FunnelConfig;
  @Input() outcomes: string[] = [];
  @Input() projectDetails: any;
  @Output() configChange = new EventEmitter<FunnelConfig>();

  readonly out = OUT_STAGE;
  readonly maxStages = MAX_STAGES;
  readonly maxName = MAX_STAGE_NAME;
  readonly profile = this.appService.getCurrentAccount();

  view: FunnelView = 'list';
  editing = false;
  /** Outcome picked with a click, waiting for a stage to be clicked. */
  selected = '';
  dragging = '';
  overStage: string | null = null;
  pickerStage = '';
  ticked: { [name: string]: boolean } = {};
  aiBusy = false;
  aiError = '';
  aiNotice = '';

  constructor(private funnelService: FunnelService, private appService: AppService) {}

  get stages(): FunnelStage[] {
    return displayStages(this.config, this.view);
  }

  get tray(): string[] {
    return unmappedOutcomes(this.config, this.outcomes);
  }

  get placedCount(): number {
    return this.outcomes.length - this.tray.length;
  }

  get complete(): boolean {
    return allPlaced(this.config, this.outcomes);
  }

  get keys(): string[] {
    return keyStages(this.config);
  }

  get keyNames(): string[] {
    return this.config.stages.filter(s => this.keys.indexOf(s.id) !== -1).map(s => s.name);
  }

  /** Outcomes of this project that sit in a stage. */
  in(stageId: string): string[] {
    return this.outcomes.filter(n => this.config.outcome_map[n] && this.config.outcome_map[n].stage === stageId);
  }

  isKey(stageId: string): boolean {
    return this.keys.indexOf(stageId) !== -1;
  }

  width(position: number): string {
    return this.view === 'funnel' ? bandWidth(position, this.config.stages.length) + '%' : '100%';
  }

  /** Lighter bands towards the top of a funnel; the P0 band is tinted. */
  bandBackground(stage: FunnelStage): string {
    if (this.view !== 'funnel') { return '#FFF'; }
    if (stage.pri === 0) { return '#FBE3D6'; }
    const last = this.config.stages.length - 1;
    const depth = last > 0 ? 1 - stage.pri / last : 1;
    return 'hsl(24,' + (40 + Math.round(depth * 18)) + '%,' + (97 - Math.round(depth * 10)) + '%)';
  }

  canUp(position: number): boolean { return position > 0; }
  canDown(position: number): boolean { return position < this.config.stages.length - 1; }

  setView(view: FunnelView) {
    this.view = view;
    this.editing = view === 'funnel';
  }

  toggleEditing() {
    this.editing = !this.editing;
  }

  private emit(config: FunnelConfig) {
    this.aiNotice = '';
    this.configChange.emit(config);
  }

  // Stages
  add() {
    this.emit(addStage(this.config, 'New stage'));
    this.editing = true;
  }
  remove(id: string) { this.emit(removeStage(this.config, id)); }
  move(id: string, step: number) { this.emit(moveStageInView(this.config, id, step, this.view)); }
  rename(id: string, name: string) { this.emit(renameStage(this.config, id, name)); }
  toggleKey(id: string) { this.emit(setStageKey(this.config, id, !this.isKey(id))); }

  // Outcomes
  pick(name: string) { this.selected = this.selected === name ? '' : name; }

  place(stageId: string, names: string[]) {
    if (names.length === 0) { return; }
    this.emit(moveOutcomes(this.config, names, stageId));
    this.selected = '';
    this.overStage = null;
  }

  placeSelected(stageId: string) {
    if (this.selected) { this.place(stageId, [this.selected]); }
  }

  unplace(name: string) { this.place('', [name]); }

  clearAll() { this.place('', this.outcomes.filter(n => !!this.config.outcome_map[n])); }

  dragStart(name: string, event: DragEvent) {
    this.dragging = name;
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', name);
      event.dataTransfer.effectAllowed = 'move';
    }
  }
  dragEnd() { this.dragging = ''; this.overStage = null; }
  dragOver(stageId: string, event: DragEvent) {
    if (!this.dragging) { return; }
    event.preventDefault();
    this.overStage = stageId;
  }
  drop(stageId: string, event: DragEvent) {
    event.preventDefault();
    const name = this.dragging || (event.dataTransfer ? event.dataTransfer.getData('text/plain') : '');
    this.dragging = '';
    if (name && this.outcomes.indexOf(name) !== -1) { this.place(stageId, [name]); }
  }

  // Add outcomes picker
  togglePicker(stageId: string) {
    this.pickerStage = this.pickerStage === stageId ? '' : stageId;
    this.ticked = {};
  }
  tick(name: string) { this.ticked[name] = !this.ticked[name]; }
  get tickedNames(): string[] { return this.tray.filter(n => this.ticked[n]); }
  selectAll() {
    const all = this.tickedNames.length === this.tray.length;
    this.ticked = {};
    this.tray.forEach(n => (this.ticked[n] = !all));
  }
  addTicked(stageId: string) {
    this.place(stageId, this.tickedNames);
    this.pickerStage = '';
    this.ticked = {};
  }

  /** Asks the server to place the outcomes that are still in the tray, or all of them when none are. */
  placeWithAi() {
    if (this.aiBusy || !this.profile || !this.projectDetails || this.outcomes.length === 0) { return; }
    const targets = this.tray.length ? this.tray : this.outcomes;
    this.aiBusy = true;
    this.aiError = '';
    this.aiNotice = '';
    this.funnelService.suggestPlacement(this.profile, this.projectDetails.project_id, this.config.stages, targets).subscribe(
      res => {
        this.aiBusy = false;
        const result = applyPlacements(this.config, targets, res.placements);
        this.configChange.emit(result.config);
        this.aiNotice = 'AI placed ' + result.placed + ' outcome' + (result.placed === 1 ? '' : 's') + '. Review and change anything.';
      },
      err => {
        this.aiBusy = false;
        this.aiError = (err && err.error && err.error.message) || 'Could not place outcomes with AI. Place them yourself, or try again.';
      }
    );
  }

  trackByIndex(i: number) { return i; }
  trackById(_: number, s: FunnelStage) { return s.id; }
}
